/* eslint-disable default-param-last */
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormHelpers } from 'ontraccr-common';

import FormFieldAdd from './FormFieldAdd';
import FormField from './FormField';
import DroppableWrapper from '../DroppableWrapper';

export default function FormFieldList({
  id,
  projectId,
  costcodes,
  costcodeIdMap,
  costcodesLoading,
  fields = [],
  onSave,
  onDelete,
  isDisplay = false,
  isAdd = false,
  sections = [],
  isBoardCards,
  isExternalForm,
  isMultiDivisionForm,
  divisions,
  templateId,
  name,
  shouldHideCustomRenderingFields = false,
  responses = {},
  isDetailView = false,
  canAddDynamicAttribute,
  showParentAttributeCheckbox,
  isTimeCard,
  isDragDisabled = false,
  allowRepeatingSections = false,
  isV3Form = false,
}) {
  const [showDrawer, setShowDrawer] = useState(false);
  const [editField, setEditField] = useState();
  const onEdit = useCallback((field) => () => {
    const {
      configProps: {
        shouldSavePresetTableSelections = false,
        presetData = {},
      },
    } = field;

    const initializedField = { ...field };
    initializedField.previewProps = shouldSavePresetTableSelections
      ? presetData
      : {};
    setShowDrawer(true);
    setEditField(initializedField);
  }, []);

  const onEditComplete = useCallback(() => {
    setEditField();
  }, []);

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        padding: 10,
      }}
    >
      <DroppableWrapper
        isDragDisabled={isDragDisabled}
        droppableId={id}
        type="FIELD"
      >
        {({ droppableProps, innerRef, placeholder }) => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <div
            {...droppableProps}
            ref={innerRef}
            style={{ display: 'flex', flexWrap: 'wrap', gap: '0 10px', alignItems: 'stretch' }}
          >
            {fields.map((field = {}, idx) => {
              const {
                configProps: {
                  hasConditionalRendering = false,
                  conditionalRenderingFormula,
                  width: fieldWidthConfig = '100',
                } = {},
              } = field ?? {};

              // Use calc to account for gap between half-width fields
              const fieldWidthStyle = fieldWidthConfig === '50' ? 'calc(50% - 5px)' : '100%';

              // If a field has conditional rendering, and the formula is complete,
              // and the formula returns false, render empty placeholder to preserve layout
              if (
                shouldHideCustomRenderingFields
                && hasConditionalRendering
                && FormHelpers.isConditionalRenderingFormulaComplete(
                  conditionalRenderingFormula,
                ) && !FormHelpers.executeConditionalRenderingFormula({
                  formula: conditionalRenderingFormula,
                  responses,
                })
              ) {
                // For full-width hidden fields or half-width fields on their own,
                // return null to collapse completely
                const previousField = fields[idx - 1] ?? {};
                const nextField = fields[idx + 1] ?? {};
                const previousWidth = previousField.configProps?.width ?? '100';
                const nextWidth = nextField.configProps?.width ?? '100';
                const isHalfWidthOnOwn = fieldWidthConfig === '50' && (previousWidth === '100' && nextWidth === '100');
                if (fieldWidthConfig === '100' || isHalfWidthOnOwn) {
                  return null;
                }

                return (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={idx}
                    style={{
                      margin: '10px 0px',
                      width: fieldWidthStyle,
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 60,
                      visibility: 'hidden',
                    }}
                  />
                );
              }

              const fieldElement = (
                <FormField
                  projectId={projectId}
                  costcodes={costcodes}
                  costcodeIdMap={costcodeIdMap}
                  costcodesLoading={costcodesLoading}
                  sectionId={id}
                  field={field}
                  index={idx}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  isDisplay={isDisplay}
                  isExternalForm={isExternalForm}
                  isMultiDivisionForm={isMultiDivisionForm}
                  divisions={divisions}
                  templateId={templateId}
                  name={name}
                  isDetailView={isDetailView}
                  draggable={!isDragDisabled}
                />
              );

              // When drag is disabled, wrap in a div to maintain consistent styling
              return isDragDisabled ? (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={idx}
                  className={isDetailView ? '' : 'form-field-preview'}
                  style={{
                    margin: '10px 0px',
                    position: 'relative',
                    width: fieldWidthStyle,
                    flexBasis: fieldWidthConfig === '100' ? '100%' : fieldWidthStyle,
                    flexGrow: 0,
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {fieldElement}
                </div>
              ) : (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={idx}>
                  {fieldElement}
                </React.Fragment>
              );
            })}
            {placeholder}
            {!isDisplay && (
              <FormFieldAdd
                field={editField}
                costcodes={costcodes}
                costcodeIdMap={costcodeIdMap}
                costcodesLoading={costcodesLoading}
                sectionId={id}
                onSave={onSave}
                onEdit={onEditComplete}
                isAdd={isAdd}
                sections={sections}
                isBoardCards={isBoardCards}
                isExternalForm={isExternalForm}
                isMultiDivisionForm={isMultiDivisionForm}
                divisions={divisions}
                templateId={templateId}
                projectId={projectId}
                name={name}
                showDrawer={showDrawer}
                setShowDrawer={setShowDrawer}
                canAddDynamicAttribute={canAddDynamicAttribute}
                showParentAttributeCheckbox={showParentAttributeCheckbox}
                isTimeCard={isTimeCard}
                allowRepeatingSections={allowRepeatingSections}
                isV3Form={isV3Form}
              />
            )}
          </div>
        )}
      </DroppableWrapper>
    </div>
  );
}

FormFieldList.propTypes = {
  id: PropTypes.string,
  projectId: PropTypes.string,
  costcodes: PropTypes.arrayOf(PropTypes.shape({})),
  costcodeIdMap: PropTypes.shape({}),
  costcodesLoading: PropTypes.bool,
  fields: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })),
  })),
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  isDisplay: PropTypes.bool,
  isAdd: PropTypes.bool,
  sections: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  isBoardCards: PropTypes.bool,
  divisions: PropTypes.arrayOf(PropTypes.string),
  templateId: PropTypes.string,
  isExternalForm: PropTypes.bool,
  name: PropTypes.string,
  shouldHideCustomRenderingFields: PropTypes.bool,
  responses: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({}),
  }),
  isDetailView: PropTypes.bool,
  canAddDynamicAttribute: PropTypes.bool,
  showParentAttributeCheckbox: PropTypes.bool,
  isTimeCard: PropTypes.bool,
  isDragDisabled: PropTypes.bool,
  allowRepeatingSections: PropTypes.bool,
  isV3Form: PropTypes.bool,
  isMultiDivisionForm: PropTypes.bool,
};

FormFieldList.defaultProps = {
  id: null,
  projectId: null,
  costcodes: [],
  costcodeIdMap: {},
  costcodesLoading: false,
  fields: [],
  onSave: null,
  onDelete: null,
  isDisplay: false,
  isAdd: false,
  sections: [],
  isBoardCards: false,
  divisions: [],
  templateId: null,
  isExternalForm: false,
  name: null,
  shouldHideCustomRenderingFields: false,
  responses: {},
  isDetailView: false,
  canAddDynamicAttribute: false,
  showParentAttributeCheckbox: false,
  isTimeCard: false,
  isDragDisabled: false,
  allowRepeatingSections: false,
  isV3Form: false,
  isMultiDivisionForm: false,
};
